import React, { useState, useRef, useEffect } from 'react';
import { IconChevronDown } from '@tabler/icons-react';

const AutocompleteDropdown = ({
  options = [],
  selected,
  setSelected,
  placeholder = 'Select...',
  className = '',
  onLoadMore,
  isLoadingMore = false,
  hasMore = false,
  label,
  required,
  onClear,
  width,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const filteredOptions = options?.filter((option) =>
    option.name.toLowerCase().includes(inputValue.toLowerCase()),
  );

  useEffect(() => {
    if (!showDropdown) return;
    const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollHeight - scrollTop - clientHeight < 10 && hasMore && !isLoadingMore) {
        if (onLoadMore) onLoadMore();
      }
    };
    const dropdown = dropdownRef.current;
    if (dropdown) {
      dropdown.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (dropdown) {
        dropdown.removeEventListener('scroll', handleScroll);
      }
    };
  }, [showDropdown, hasMore, isLoadingMore, onLoadMore]);

  const handleSelect = (option) => {
    setSelected(option);
    setInputValue(option.name);
    setShowDropdown(false);
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex items-center justify-between gap-1">
        {label && <label className="block text-sm font-medium leading-6">{label || 'Type'}</label>}
        {required && <label className="block text-xs font-light leading-6">(required)</label>}
      </div>

      <div
        className={`${
          width ? width : 'w-[200px]'
        } relative flex flex-row gap-2 items-center bg-white rounded-lg`}>
        <div className="w-full flex flex-row justify-between px-1.5 items-center gap-2 min-h-[36px] h-[36px] cursor-pointer shadow-sm">
          <input
            type="text"
            className="w-full bg-transparent outline-none font-semibold text-kin-text placeholder:text-gray-400"
            placeholder={placeholder}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onBlur={() => setTimeout(() => setShowDropdown(false), 100)}
            style={{ border: 'none', boxShadow: 'none' }}
          />
          {selected && (
            <button
              type="button"
              onClick={() => {
                setSelected(null);
                setInputValue('');
                onClear?.();
              }}
              className="text-kin-text"
              title="Clear">
              ✕
            </button>
          )}

          <span className="text-gray-500 w-5 h-5">
            <span className="cursor-pointer" onClick={() => setShowDropdown((prev) => !prev)}>
              <IconChevronDown className="w-5 h-5" />
            </span>
          </span>
        </div>
        {/* Dropdown Options */}
        {showDropdown && (
          <div className={`${width ? width : 'w-[200px]'} absolute top-[40px] z-10`}>
            <div
              ref={dropdownRef}
              className="bg-white flex flex-col py-1 shadow-lg rounded-md max-h-[200px] overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-gray-400 text-sm">No options found</div>
              ) : (
                filteredOptions.map((option) => {
                  const activeItem = selected?.id === option.id;
                  return (
                    <div
                      key={option.id}
                      onMouseDown={() => handleSelect(option)}
                      className={`${
                        activeItem
                          ? 'font-bold bg-primary-main text-white'
                          : 'text-kin-text font-normal'
                      } cursor-pointer px-4 py-2 flex flex-row items-center justify-start hover:bg-primary-main hover:text-white`}>
                      {option.name}
                      {activeItem && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="ml-1 w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
              {isLoadingMore && (
                <div className="px-4 py-2 text-gray-400 text-sm text-center">Loading more...</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutocompleteDropdown;
